import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import ActivityWizard from "../Activity/ActivityWizard";
import CourseWizard from "../Course/CourseWizard";
import PathWizard from "../Path/PathWizard";
import { useDraftDetail } from "../hooks/useDraftDetail";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { usePromoteDraft } from "../hooks/usePromoteDraft";
import {
  DraftErrorInfo,
  extractPromoteError,
} from "../hooks/parsePromoteErrors";
import DraftPromoteErrorDialog from "../components/DraftPromoteErrorDialog";

const EditDraft: React.FC = () => {
  const { t } = useTranslation("catalog-v2");
  const navigate = useNavigate();
  const { id: corporateId = "", idDraft = "" } = useParams();
  const draftId = Number(idDraft);
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const { wizard, type, isLoading, error } = useDraftDetail({
    corporateId,
    draftId: idDraft,
  });
  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: type ?? "activity",
    initialDraftId: draftId,
  });
  const { promoteDraft } = usePromoteDraft({ corporateId });

  const [isPublishing, setIsPublishing] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [promoteError, setPromoteError] = useState<DraftErrorInfo | null>(null);

  const handleSaveDraft = async (data: any) => {
    try {
      await saveDraft(data);
      navigate(`${catalogUrl}?tab=all`);
    } catch (err) {
      console.error("[edit-draft] saveDraft failed", err);
      throw err;
    }
  };

  const handlePublish = async (data: any) => {
    setIsPublishing(true);
    try {
      await saveDraft(data);
      try {
        await promoteDraft(draftId);
      } catch (err) {
        setPromoteError(extractPromoteError(err));
        throw err;
      }
      setSuccessOpen(true);
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !wizard || !type) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <Typography>{t("draft-error")}</Typography>
      </Box>
    );
  }

  const wizardProps = {
    initialData: wizard,
    onComplete: handlePublish,
    onSaveDraft: handleSaveDraft,
    isSavingDraft,
    isSubmitting: isPublishing,
    mode: "add" as const,
    syncNavbarInitiative: false,
  };

  return (
    <>
      {type === "activity" && <ActivityWizard {...wizardProps} />}
      {type === "course" && <CourseWizard {...wizardProps} />}
      {type === "path" && <PathWizard {...wizardProps} />}
      <LOOutcomeModalCst
        open={successOpen}
        entityType={type}
        mode="add"
        onDismiss={() => navigate(catalogUrl)}
      />
      <DraftPromoteErrorDialog
        error={promoteError}
        onClose={() => setPromoteError(null)}
      />
    </>
  );
};

export default EditDraft;
